<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import BaseIconLoader from '@/presentation/components/base/iconLoader/BaseIconLoader.vue';
import type { MainNavigationItem } from '@/presentation/layouts/useMainNavigationItems';

interface IProps {
	items: MainNavigationItem[];
	activeRouteName: string;
}

const props = defineProps<IProps>();

const route = useRoute();
const router = useRouter();

const navContainerRef = ref<HTMLElement | null>(null);
const tabButtonRefs = ref<HTMLElement[]>([]);

const activeIndex = computed(() => {
	const currentName = String(route.name || props.activeRouteName || '');
	const currentPath = String(route.path || '');

	// Find best match by customRuleActivation, routeName, or routeAddress
	const index = props.items.findIndex((item) => {
		if (item.customRuleActivation) return true;
		if (item.routeName && item.routeName === currentName) return true;
		if (item.routeAddress && item.routeAddress === currentPath) return true;
		return false;
	});

	if (index !== -1) return index;

	// Fallback match prefix (e.g. /category/123 matches /category)
	const prefixIndex = props.items.findIndex((item) => {
		if (item.routeAddress && item.routeAddress !== '/' && currentPath.startsWith(item.routeAddress)) {
			return true;
		}
		return false;
	});

	return prefixIndex !== -1 ? prefixIndex : 0;
});

const activeItem = computed(() => props.items[activeIndex.value] || props.items[0]);

// Animated position states
const notchLeft = ref<number>(0);
const circleLeft = ref<number>(0);
const isReady = ref(false);

const updatePositions = () => {
	if (!navContainerRef.value || !tabButtonRefs.value.length) return;

	const targetBtn = tabButtonRefs.value[activeIndex.value];
	if (!targetBtn) return;

	const containerRect = navContainerRef.value.getBoundingClientRect();
	const btnRect = targetBtn.getBoundingClientRect();

	// Calculate center relative to nav container
	const center = btnRect.left - containerRect.left + btnRect.width / 2;

	// Notch width is 76px, so offset is center - 38px
	notchLeft.value = Math.round(center - 38);

	// Floating circle diameter is 48px, so offset is center - 24px
	circleLeft.value = Math.round(center - 24);
};

const onItemClick = (item: MainNavigationItem) => {
	if (item.routeAddress) {
		router.push(item.routeAddress);
	}
};

watch(
	activeIndex,
	() => {
		nextTick(updatePositions);
	},
	{ immediate: false },
);

watch(
	() => props.items,
	() => {
		nextTick(updatePositions);
	},
	{ deep: true },
);

let resizeObserver: ResizeObserver | null = null;

onMounted(() => {
	nextTick(() => {
		updatePositions();
		// Allow first render without jarring transitions
		setTimeout(() => {
			isReady.value = true;
		}, 60);
	});

	if (typeof window !== 'undefined' && 'ResizeObserver' in window && navContainerRef.value) {
		resizeObserver = new ResizeObserver(() => {
			updatePositions();
		});
		resizeObserver.observe(navContainerRef.value);
	}

	window.addEventListener('resize', updatePositions);
});

onUnmounted(() => {
	if (resizeObserver) {
		resizeObserver.disconnect();
	}
	window.removeEventListener('resize', updatePositions);
});
</script>

<template>
	<nav dir="rtl" data-business-tour="mobile-nav" class="fixed inset-x-0 bottom-0 z-40 md:hidden select-none" aria-label="منوی دسترسی سریع">
		<!-- Outer shadow layer & precise width reference container -->
		<div
			ref="navContainerRef"
			class="relative w-full max-w-lg mx-auto filter drop-shadow-[0_-6px_20px_rgba(72,72,254,0.10)] drop-shadow-[0_-2px_6px_rgba(15,23,42,0.04)]"
		>
			<!-- Top Cutout Bar Row (Height: 30px) - explicitly dir="ltr" so left filler starts on the physical left -->
			<div dir="ltr" class="relative flex h-[30px] w-full items-stretch overflow-hidden">
				<!-- Left white filler with top border -->
				<div
					class="h-[30px] bg-white border-t border-slate-200/70 transition-[width] duration-350 ease-[cubic-bezier(0.34,1.35,0.64,1)]"
					:style="{ width: `${Math.max(0, notchLeft)}px` }"
				></div>

				<!-- Curved Notch SVG (Width: 76px, Height: 30px) -->
				<div class="h-[30px] w-[76px] shrink-0">
					<svg width="76" height="30" viewBox="0 0 76 30" fill="none" xmlns="http://www.w3.org/2000/svg" class="notch-svg block h-[30px] w-[76px]">
						<!-- Smooth mathematically continuous Bézier cradle matching mockup -->
						<path d="M0 0C13 0 17 24 38 24C59 24 63 0 76 0L76 30L0 30Z" fill="white" />
						<!-- Continuous top border line tracing the curve -->
						<path d="M0 0.5C13 0.5 17 24.5 38 24.5C59 24.5 63 0.5 76 0.5" fill="none" stroke="#e2e8f0" stroke-width="1" />
					</svg>
				</div>

				<!-- Right white filler with top border -->
				<div class="h-[30px] flex-1 bg-white border-t border-slate-200/70"></div>
			</div>

			<!-- Bottom Solid White Bar (Height: 46px + safe-area) -->
			<div class="h-[46px] w-full bg-white pb-[env(safe-area-inset-bottom,0px)]"></div>

			<!-- Floating Elevated Circular Action Button (Inside the notch) -->
			<div
				class="pointer-events-none absolute left-0 top-[-13px] z-20 flex h-[48px] w-[48px] items-center justify-center rounded-full bg-[#4848FE] text-white ring-4 ring-white shadow-[0_8px_22px_rgba(72,72,254,0.40)] transition-transform duration-350 ease-[cubic-bezier(0.34,1.35,0.64,1)]"
				:class="{ 'opacity-0 scale-75': !isReady, 'opacity-100 scale-100': isReady }"
				:style="{ transform: `translateX(${circleLeft}px)` }"
				aria-hidden="true"
			>
				<Transition name="icon-pop" mode="out-in">
					<span :key="activeItem?.routeName || activeIndex" class="flex items-center justify-center">
						<BaseIconLoader v-if="activeItem?.iconName" :name="activeItem.iconName" size="22px" />
					</span>
				</Transition>
			</div>

			<!-- Tab Clickable Items Row -->
			<div class="absolute inset-x-0 bottom-0 top-0 z-30 flex items-center justify-around pb-[env(safe-area-inset-bottom,0px)]">
				<button
					v-for="(item, index) in items"
					:key="item.routeName"
					:ref="(el) => (tabButtonRefs[index] = el as HTMLElement)"
					type="button"
					class="relative flex h-full flex-1 flex-col items-center justify-center pt-2 transition-transform active:scale-90 focus:outline-none"
					:aria-label="item.label"
					:aria-current="index === activeIndex ? 'page' : undefined"
					:data-business-tour="`nav-${item.routeName}`"
					@click="onItemClick(item)"
				>
					<!-- Inactive Icon (Fades and scales out when active) -->
					<div
						class="flex flex-col items-center justify-center transition-all duration-300"
						:class="
							index === activeIndex
								? 'opacity-0 scale-50 translate-y-3 pointer-events-none'
								: 'opacity-100 scale-100 translate-y-0 text-[#4848FE] hover:text-[#3636e0]'
						"
					>
						<span class="relative inline-flex h-6 w-6 items-center justify-center">
							<BaseIconLoader :name="item.iconName" size="22px" />
							<!-- Pending Badge Dot -->
							<span v-if="item.hasBadge" class="absolute -left-1 -top-1 h-2.5 w-2.5 rounded-full bg-[#ff2d2d] ring-2 ring-white" aria-hidden="true" />
						</span>
						<span class="mt-0.5 text-[10px] font-bold text-slate-600 leading-3 max-w-[64px] truncate">
							{{ item.label }}
						</span>
					</div>
				</button>
			</div>
		</div>
	</nav>
</template>

<style scoped>
/* Icon pop animation when switching tabs */
.icon-pop-enter-active {
	animation: pop-in 0.28s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.icon-pop-leave-active {
	animation: pop-out 0.15s ease-in;
}

@keyframes pop-in {
	0% {
		opacity: 0;
		transform: scale(0.5);
	}
	100% {
		opacity: 1;
		transform: scale(1);
	}
}

@keyframes pop-out {
	0% {
		opacity: 1;
		transform: scale(1);
	}
	100% {
		opacity: 0;
		transform: scale(0.6);
	}
}

:deep(svg:not(.notch-svg)),
:deep(svg:not(.notch-svg) path) {
	fill: currentColor;
}

.notch-svg path {
	fill: #ffffff !important;
}
</style>
